import { useState, useEffect, useCallback, useRef } from 'react';
import { Alert, Box } from '@mui/material';
import { Grid2 } from '@mui/material';
import whatsappService from '../services/whatsappService';
import useSnackbar from '../hooks/useSnackbar';
import PageLayout from '../components/ui/PageLayout';
import AppSnackbar from '../components/ui/AppSnackbar';
import ProductCatalog from '../components/billing/ProductCatalog';
import { useMeter } from '../hooks/useEntitlements';
import { WhatsApp as WhatsAppIcon } from '@mui/icons-material';
import { pageContainer } from '../styles/pageStyles';

import WhatsAppSettingsCard from './integrations/WhatsAppSettingsCard';
import MessageHistoryCard from './integrations/MessageHistoryCard';
import WhatsAppPreviewAside from './integrations/WhatsAppPreviewAside';

const DEFAULT_SETTINGS = {
  enabled: false,
  templateStyle: 2,
  messageNotes: '',
  notifyOnPublicCreate: true,
  notifyOnPrivateCreate: true,
  reminderEnabled: false,
  reminderHoursBefore: 24,
  reminderRespectBusinessHours: true,
  reminderBusinessStart: 8,
  reminderBusinessEnd: 18,
  ownerNotifyEnabled: false,
  ownerPhone: '',
  ownerNotifyOnCreate: true,
  ownerNotifyOnConfirm: true,
  ownerNotifyOnCancel: true,
};

function clampHour(v, fallback) {
  const n = parseInt(v, 10);
  if (Number.isNaN(n)) return fallback;
  return Math.min(24, Math.max(0, n));
}

export default function IntegrationsPage() {
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();
  const { balance: waBalance, low: waLow } = useMeter('whatsapp_messages');
  const catalogRef = useRef(null);

  // ── Settings state ──────────────────────────────────────────────────────────
  const [form, setForm]                       = useState(DEFAULT_SETTINGS);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingSettings, setSavingSettings]   = useState(false);
  const [models, setModels]                   = useState([]);

  // ── Messages state ──────────────────────────────────────────────────────────
  const [messages, setMessages]   = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState(null);
  const [summary, setSummary]     = useState(null);

  // ── Load settings on mount ──────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const data = await whatsappService.getSettings();
        setForm({ ...DEFAULT_SETTINGS, ...data, messageNotes: data.messageNotes ?? '', ownerPhone: data.ownerPhone ?? '' });
        try { setModels(await whatsappService.getModels()); } catch { /* nomes/preview caem no fallback */ }
      } catch {
        // Silencioso — mantém defaults
      } finally {
        setLoadingSettings(false);
      }
    })();
  }, []);

  // ── Load messages when page/rowsPerPage change ──────────────────────────────
  const loadMessages = useCallback(async (p, rpp) => {
    setLoadingMessages(true);
    setMessagesError(null);
    try {
      const data = await whatsappService.listMessages(p + 1, rpp); // API é 1-indexed
      setMessages(data.items ?? []);
      setTotal(data.total ?? 0);
      setSummary(data.summary ?? null);
    } catch {
      setMessagesError('Não foi possível carregar o histórico. Tente novamente.');
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    loadMessages(page, rowsPerPage);
  }, [page, rowsPerPage, loadMessages]);

  // ── Form helpers ────────────────────────────────────────────────────────────
  const updateField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSave = useCallback(async () => {
    setSavingSettings(true);
    try {
      await whatsappService.updateSettings({
        ...form,
        messageNotes: form.messageNotes?.trim() ? form.messageNotes.trim() : null,
        reminderHoursBefore: Math.min(168, Math.max(1, parseInt(form.reminderHoursBefore, 10) || 24)),
        reminderBusinessStart: clampHour(form.reminderBusinessStart, 8),
        reminderBusinessEnd: clampHour(form.reminderBusinessEnd, 18),
      });
      showSuccess('Configurações de WhatsApp salvas.');
    } catch {
      showError('Não foi possível salvar as configurações. Tente novamente.');
    } finally {
      setSavingSettings(false);
    }
  }, [form, showSuccess, showError]);

  // ── Pagination handlers ─────────────────────────────────────────────────────
  const handlePageChange = (_, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleScrollToCatalog = () => {
    catalogRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // ── Model para preview ──────────────────────────────────────────────────────
  const selectedModel = models.find((m) => m.id === form.templateStyle);

  return (
    <PageLayout
      title="Integrações"
      subtitle="Gerencie suas integrações externas"
      icon={<WhatsAppIcon sx={{ color: 'primary.main' }} />}
    >
      <Box sx={pageContainer}>
        {/* Grid 2 colunas: config (xs:12, lg:8) + aside sticky (xs:12, lg:4) */}
        <Grid2 container spacing={3}>
          {/* Coluna esquerda: Settings Card */}
          <Grid2 size={{ xs: 12, lg: 8 }}>
            <WhatsAppSettingsCard
              loadingSettings={loadingSettings}
              savingSettings={savingSettings}
              form={form}
              updateField={updateField}
              handleSave={handleSave}
              models={models}
            />
          </Grid2>

          {/* Aside direita: Preview + Saldo + Ver Pacotes */}
          <Grid2 size={{ xs: 12, lg: 4 }}>
            <WhatsAppPreviewAside
              selectedModel={selectedModel}
              messageNotes={form.messageNotes}
              waBalance={waBalance}
              onScrollToCatalog={handleScrollToCatalog}
            />
          </Grid2>
        </Grid2>

        {/* ── Alerta de saldo — gatilho de venda quando baixo/zerado ─── */}
        {form.enabled && (waBalance === 0 || waLow) && (
          <Alert severity={waBalance === 0 ? 'error' : 'warning'} sx={{ mt: 3, mb: 3 }}>
            {waBalance === 0
              ? 'Você está sem saldo de mensagens. Compre um pacote abaixo para voltar a enviar pelo WhatsApp.'
              : `Seu saldo de mensagens está baixo (${waBalance}). Garanta um pacote abaixo para não ficar sem.`}
          </Alert>
        )}

        {/* ── Vitrine de pacotes (PIX embeddado) ───────────────────────────── */}
        <Box ref={catalogRef} sx={{ mt: 4 }}>
          <ProductCatalog meter="whatsapp_messages" />
        </Box>

        {/* ── Histórico ────────────────────────────────────────────────────── */}
        <Box sx={{ mt: 4 }}>
          <MessageHistoryCard
            messages={messages}
            total={total}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            loading={loadingMessages}
            error={messagesError}
            setError={setMessagesError}
            summary={summary}
          />
        </Box>
      </Box>

      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </PageLayout>
  );
}
